// ContractBlockingGuard.js - Middleware to enforce contract acceptance
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Routes that are exempt from contract blocking
const EXEMPT_ROUTES = [
  '/contract-review',
  '/login',
  '/register',
  '/logout',
  '/forgot-password',
  '/downloads',
  '/',  // Landing page
];

export const ContractBlockingGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const mountedRef = useRef(true);
  
  // Start with checking=false for exempt routes to avoid unnecessary re-renders
  const isExemptRoute = EXEMPT_ROUTES.some(route => location.pathname.startsWith(route));
  const [checking, setChecking] = useState(!isExemptRoute);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    
    // Skip check for exempt routes - don't even set state
    if (EXEMPT_ROUTES.some(route => location.pathname.startsWith(route))) {
      return;
    }

    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (!token) {
      if (mountedRef.current) {
        setChecking(false);
        setBlocked(false);
      }
      return;
    }

    const controller = new AbortController();

    const checkContractStatus = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/contracts/acceptance/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (mountedRef.current && response.ok) {
          const data = await response.json();
          
          if (data.requires_acceptance) {
            setBlocked(true);
            setChecking(false);
            // Redirect to contract review if not already there
            if (location.pathname !== '/contract-review') {
              navigate('/contract-review', { replace: true });
            }
          } else {
            setBlocked(false);
            setChecking(false);
          }
        } else if (mountedRef.current) {
          setChecking(false);
          setBlocked(false);
        }
      } catch (err) {
        if (err.name !== 'AbortError' && mountedRef.current) {
          console.error('Contract check failed:', err);
          // Don't block on error
          setBlocked(false);
          setChecking(false);
        }
      }
    };

    checkContractStatus();

    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, [location.pathname, navigate]);

  // Show nothing while checking (only for non-exempt routes)
  if (checking) {
    return null;
  }

  // Show blocking message if blocked and somehow still on a protected route
  if (blocked && !EXEMPT_ROUTES.some(route => location.pathname.startsWith(route))) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Vertragsannahme erforderlich</strong>
            <br />
            Bitte akzeptieren Sie zuerst die aktuellen Vertragsbedingungen, um CashX weiter zu nutzen.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return children;
};

export default ContractBlockingGuard;
